import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3, Megaphone, X } from "lucide-react";

/**
 * Banner LGPD de consentimento de cookies.
 *
 * Categorias granulares:
 *  - essential (sempre ativa)
 *  - analytics (GA4, Search Console)
 *  - marketing (Google Ads, Meta Pixel)
 *
 * Persistência: localStorage.raf_consent_v1 = {
 *   essential: true,
 *   analytics: bool,
 *   marketing: bool,
 *   ts: ISO string,
 *   v: 1
 * }
 *
 * Integrações futuras podem checar `window.rafConsent.get()`
 * ou ouvir o evento `raf:consent-updated`.
 */

const STORAGE_KEY = "raf_consent_v1";
const CONSENT_VERSION = 1;

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.v !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

// Propaga o consentimento para o Google Consent Mode v2. O tag do Google Ads
// (AW-18377265401) começa em "denied" no index.html e só coleta dados de
// marketing/analytics quando o usuário consente aqui.
const applyGoogleConsent = (consent) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const analytics = consent.analytics ? "granted" : "denied";
  const marketing = consent.marketing ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: analytics,
  });
};

const writeConsent = (consent) => {
  const payload = { ...consent, v: CONSENT_VERSION, ts: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {}
  window.rafConsent = { get: () => payload };
  applyGoogleConsent(payload);
  window.dispatchEvent(new CustomEvent("raf:consent-updated", { detail: payload }));
  return payload;
};

// Torna disponível globalmente (para futuras integrações de GA4/Pixel)
if (typeof window !== "undefined") {
  const existing = readConsent();
  window.rafConsent = {
    get: () => readConsent(),
    open: () => window.dispatchEvent(new CustomEvent("raf:consent-open")),
  };
  if (existing) {
    // Reaplica consentimento salvo (Google Consent Mode v2) e dispara evento
    // inicial para integrações que já foram carregadas.
    setTimeout(() => {
      applyGoogleConsent(existing);
      window.dispatchEvent(new CustomEvent("raf:consent-updated", { detail: existing }));
    }, 0);
  }
}

const CategoryRow = ({ icon: Icon, title, description, checked, onChange, disabled, testid }) => (
  <label
    className={`flex items-start gap-3 rounded-xl border p-4 transition ${
      disabled ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200 hover:border-[#0B2B62]"
    }`}
    data-testid={testid}
  >
    <div className="h-9 w-9 rounded-lg bg-[#0B2B62]/10 text-[#0B2B62] flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-900 text-sm">{title}</span>
        {disabled && (
          <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
            sempre ativo
          </span>
        )}
      </div>
      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{description}</p>
    </div>
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked)}
      className="h-5 w-5 mt-1 accent-[#0B2B62] shrink-0"
      data-testid={testid + "-checkbox"}
    />
  </label>
);

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setVisible(true);
    const openHandler = () => {
      const cur = readConsent();
      if (cur) setPrefs({ analytics: !!cur.analytics, marketing: !!cur.marketing });
      setCustomize(true);
      setVisible(true);
    };
    window.addEventListener("raf:consent-open", openHandler);
    return () => window.removeEventListener("raf:consent-open", openHandler);
  }, []);

  const acceptAll = useCallback(() => {
    writeConsent({ essential: true, analytics: true, marketing: true });
    setVisible(false);
    setCustomize(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    writeConsent({ essential: true, analytics: false, marketing: false });
    setVisible(false);
    setCustomize(false);
  }, []);

  const saveCustom = useCallback(() => {
    writeConsent({ essential: true, analytics: prefs.analytics, marketing: prefs.marketing });
    setVisible(false);
    setCustomize(false);
  }, [prefs]);

  if (!visible) return null;

  return (
    <>
      {/* Overlay leve quando modal aberto */}
      {customize && (
        <div
          className="fixed inset-0 z-[59] bg-slate-900/40 backdrop-blur-sm"
          onClick={() => setCustomize(false)}
        />
      )}

      {/* Container do banner */}
      <div
        className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4"
        role="dialog"
        aria-label="Preferências de cookies"
        data-testid="cookie-banner"
      >
        <div className="max-w-5xl mx-auto rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden">
          {/* MODO SIMPLES */}
          {!customize && (
            <div className="p-5 sm:p-6 grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-[#0B2B62]/10 text-[#0B2B62] flex items-center justify-center shrink-0">
                  <Cookie className="h-5 w-5" />
                </div>
                <div className="text-sm text-slate-700 leading-relaxed">
                  <div className="font-semibold text-slate-900">Usamos cookies para melhorar sua experiência</div>
                  <p className="mt-1">
                    Utilizamos cookies essenciais para o funcionamento do site e, com sua permissão,
                    cookies de análise e marketing para entender o uso e apoiar nossas campanhas de
                    conscientização. Você pode aceitar todos, rejeitar os não essenciais ou personalizar.
                    Saiba mais na{" "}
                    <Link to="/politica-de-privacidade" className="text-[#0B2B62] underline">
                      Política de Privacidade
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-2 md:min-w-[260px]">
                <button
                  type="button"
                  onClick={rejectNonEssential}
                  data-testid="cookie-reject"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                >
                  Rejeitar não-essenciais
                </button>
                <button
                  type="button"
                  onClick={() => setCustomize(true)}
                  data-testid="cookie-customize"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                >
                  Personalizar
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                  className="rounded-lg bg-[#0B2B62] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2454] transition"
                >
                  Aceitar todos
                </button>
              </div>
            </div>
          )}

          {/* MODO PERSONALIZAR */}
          {customize && (
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
                    Preferências de cookies
                  </div>
                  <h3 className="mt-1 text-lg font-bold text-[#0B2B62]">Escolha o que aceita</h3>
                  <p className="mt-1 text-xs text-slate-500 max-w-lg">
                    Sua escolha fica registrada neste navegador. Você pode alterar a qualquer momento
                    pelo link "Preferências de cookies" no rodapé.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCustomize(false)}
                  aria-label="Fechar"
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                  data-testid="cookie-close-customize"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <CategoryRow
                  icon={ShieldCheck}
                  title="Essenciais"
                  description="Necessários para o site funcionar: autenticação, sessão, segurança e preferências básicas. Não podem ser desativados."
                  checked={true}
                  disabled={true}
                  testid="cookie-cat-essential"
                />
                <CategoryRow
                  icon={BarChart3}
                  title="Análise / Estatística"
                  description="Nos ajudam a entender de forma agregada como o site é usado (páginas visitadas, tempo, dispositivos) para melhorar o serviço. Ex.: Google Analytics."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  testid="cookie-cat-analytics"
                />
                <CategoryRow
                  icon={Megaphone}
                  title="Marketing"
                  description="Permitem medir conversões e exibir anúncios relevantes de forma responsável. Ex.: Google Ads, Meta Pixel. Nunca envolvem conteúdo sensível do seu caso."
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  testid="cookie-cat-marketing"
                />
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-end">
                <button
                  type="button"
                  onClick={rejectNonEssential}
                  data-testid="cookie-reject-inside"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                >
                  Rejeitar não-essenciais
                </button>
                <button
                  type="button"
                  onClick={saveCustom}
                  data-testid="cookie-save-custom"
                  className="rounded-lg bg-[#0B2B62] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2454] transition"
                >
                  Salvar preferências
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  data-testid="cookie-accept-all-inside"
                  className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 transition"
                >
                  Aceitar todos
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
